/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import {
    TenantJob,
    TenantJobStatusCodes,
    TenantJobType
} from 'application/Shared/types/tenantJob';

/*  NPM  */
import axios from 'axios';

/*  React  */
import { useEffect, useState } from 'react';

interface TenantJobResponseData {
    data: TenantJob[];
    message: string;
    serverResponse: number;
}

export const useFetchTenantJobs = ({
    clientId,
    entityId,
    status,
    type
}: {
    clientId: string;
    entityId?: string;
    status?: TenantJobStatusCodes;
    type?: TenantJobType;
}) => {
    const [tenantJobs, setTenantJobs] = useState<TenantJob[]>([]);

    const [loading, setLoading] = useState(false);
    const [refetching, setRefetching] = useState(false);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => {
        setShouldRefetch({});
        setRefetching(true);
    };

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params = {
            entityId,
            platformTenantId: clientId,
            size: -1,
            status,
            type
        };

        const fetchData = async () => {
            setLoading(true);
            if (!clientId || clientId === '0') {
                setLoading(false);
                setTenantJobs([]);
                return;
            }
            try {
                const response = await axios.get<TenantJobResponseData>(
                    `/tenantJob`,
                    {
                        cancelToken: source.token,
                        params
                    }
                );
                if (!Response.hasData(response)) {
                    debug.e('No Tenant Jobs Found.', response);
                    setLoading(false);
                    setTenantJobs([]);
                    return;
                }

                setTenantJobs(Response.getData(response));
                setLoading(false);
            } catch (err) {
                debug.e('Unable to fetch Tenant Jobs.', err);
                setLoading(false);
                setTenantJobs([]);
            }
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, entityId, status, shouldRefetchData, type]);

    return {
        loading,
        refetchData,
        refetching,
        tenantJobs
    };
};

export const useFetchTenantJob = ({
    clientId,
    entityId,
    id,
    status,
    type
}: {
    clientId: string;
    entityId?: string;
    id?: string;
    status?: TenantJobStatusCodes;
    type?: TenantJobType;
}) => {
    const [tenantJob, setTenantJob] = useState<TenantJob | null>(null);

    const [loading, setLoading] = useState(false);
    const [refetching, setRefetching] = useState(false);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchData = () => {
        setShouldRefetch({});
        setRefetching(true);
    };

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params = {
            entityId,
            id,
            platformTenantId: clientId,
            size: 1,
            status,
            type
        };

        const fetchData = async () => {
            setLoading(true);
            if (!clientId || clientId === '0' || (!entityId && !id)) {
                setLoading(false);
                setTenantJob(null);
                return;
            }
            try {
                const response = await axios.get<TenantJobResponseData>(
                    `/tenantJob`,
                    {
                        cancelToken: source.token,
                        params
                    }
                );
                if (!Response.hasData(response)) {
                    setLoading(false);
                    setTenantJob(null);
                    return;
                }

                setTenantJob(Response.getData(response)[0]);
                setLoading(false);
            } catch (err) {
                if (!axios.isCancel(err)) {
                    debug.e('Unable to fetch Tenant Jobs.', err);
                    setLoading(false);
                }
                setTenantJob(null);
            }
        };
        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, entityId, status, shouldRefetchData, type, id]);

    return {
        loading,
        refetchData,
        refetching,
        tenantJob
    };
};
